'use client'

import {ReactNode, Suspense, useEffect} from 'react'
import {usePathname} from 'next/navigation'
import SiteHeader from '@/components/v9/SiteHeader'
import SiteFooter from '@/components/v9/SiteFooter'
import {AppHandoffProvider} from '@/components/v9/AppHandoff'
import RouteProgress from '@/components/v9/RouteProgress'

type Props = {
    children: ReactNode
}

export default function HeaderFooterWrapper({children}: Props) {
    const pathname = usePathname()
    // /p/<slug> and /pay/<code>: planner storefronts and payment links stand on their own, with no route out to Mila's catalog.
    const hideHeaderFooter = pathname.includes('/auth') || pathname.includes('/book-external') || pathname.includes('/partner-pay') || /(^|\/)(p|pay)\//.test(pathname)
    // The home hero runs under the transparent header; every other page starts below it.
    // The home is '/' once the locale prefix is gone. next/navigation gives the internal path, '/ar' for the Arabic
    // root, so the old check missed it: main got the header's padding, the hero sat below the header, and the header
    // (which does strip the locale) painted white text over that light band at the top (Mohammed, 2026-10-25).
    const isHome = pathname.replace(/^\/(ar|en)(?=\/|$)/, '') === '' || pathname.replace(/^\/(ar|en)(?=\/|$)/, '') === '/'

    // The web cart is gone (services are ordered in the app): drop what old visits left in the browser.
    useEffect(() => {
        try {
            localStorage.removeItem('mila-cart-storage')
            localStorage.removeItem('mila-venue-cart-storage')
        } catch { /* storage blocked */ }
    }, [])

    return (
        <AppHandoffProvider>
            {/* The gold line at the top while the next page loads. It reads the URL, which a prerendered page only has
                in the browser: the boundary keeps that out of the page's HTML. */}
            <Suspense fallback={null}><RouteProgress/></Suspense>
            {!hideHeaderFooter && <SiteHeader/>}
            {hideHeaderFooter ? children : (
                <main id="main" className={isHome ? '' : 'pt-[var(--hdr)]'}>{children}</main>
            )}
            {!hideHeaderFooter && <SiteFooter/>}
        </AppHandoffProvider>
    )
}
